/**
 * Request Board — backend details.
 *
 * When the frontend dev can't finish a card without a backend change, the card
 * moves to Requires Backend Changes through this dialog (it never moves without
 * it): what's needed, a one-line summary for the card, an optional markdown spec
 * (typed, pasted, imported or dropped as a .md), screenshots / files, and the
 * tasks the backend team works through — `- [ ]` lines in the spec become tasks
 * automatically, and more can be added by hand.
 *
 * Stored on the card as `blocker`:
 *   { type, label, text, spec, specFile, files[], items[{ t, done }] }
 *
 * Nothing is handed to a person: the card lands in Requires Backend Changes and
 * the backend team works that column from the board.
 *
 * New files keep their File (`file`) until the dialog is saved; the board then
 * uploads them and sends their attachment ids (see useRequestBoard).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useSnackbar } from "notistack";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Collapse from "@mui/material/Collapse";
import Dialog from "@mui/material/Dialog";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import InputBase from "@mui/material/InputBase";
import useMediaQuery from "@mui/material/useMediaQuery";
import { alpha } from "@mui/material/styles";
import AddRounded from "@mui/icons-material/AddRounded";
import AttachFileRounded from "@mui/icons-material/AttachFileRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import ContentCopyRounded from "@mui/icons-material/ContentCopyRounded";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import FileUploadOutlined from "@mui/icons-material/FileUploadOutlined";
import HelpOutlineRounded from "@mui/icons-material/HelpOutlineRounded";
import InsertDriveFileOutlined from "@mui/icons-material/InsertDriveFileOutlined";
import StorageRounded from "@mui/icons-material/StorageRounded";

import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import { slate, status, steel, teal } from "../../theme/colors";
import { A } from "../admin/shared/adminTokens";
import { displayTitle, itemRef } from "./requestBoardModel";
import { CollapsibleSection } from "./requestBoardParts";

/* ─────────────────────────────── model ──────────────────────────────── */

export const BLOCKER_TYPES = [
  { key: "data", label: "Data model change", Icon: StorageRounded },
  { key: "new", label: "New endpoint", Icon: AddRounded },
  { key: "update", label: "Update an endpoint", Icon: EditOutlined },
  { key: "question", label: "Question for backend", Icon: HelpOutlineRounded },
];

export const SUMMARY_MAX = 140;

/** "412 KB", "1.2 MB", "492 B". */
export const formatBytes = (n) => {
  if (!Number.isFinite(n)) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

export const isImage = (f) => (f?.type || "").startsWith("image/");

const isMarkdownFile = (f) => /\.(md|markdown|txt)$/i.test(f?.name || "");

/** A picked / pasted / dropped File as a handoff attachment. */
const toAttachment = (file, i = 0) => ({
  id: `f${Date.now()}-${i}-${Math.random().toString(36).slice(2, 7)}`,
  name: file.name || "pasted-screenshot.png",
  type: file.type || "",
  size: file.size,
  url: URL.createObjectURL(file),
  // Uploaded on save (POST /request_board/attachments); dropped once it has an id there.
  file,
});

/** `- [ ] text` / `* [x] text` / `+ [ ] text` lines → tasks, symbols stripped. */
export const parseSpecTasks = (md) =>
  (md || "")
    .split(/\r?\n/)
    .map((line) => /^\s*[-*+]\s+\[( |x|X)\]\s+(.+?)\s*$/.exec(line))
    .filter(Boolean)
    .map((m) => ({ t: m[2].replace(/[`*_]/g, "").trim(), done: m[1].toLowerCase() === "x" }))
    .filter((x) => x.t);

const readText = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ""));
    r.onerror = reject;
    r.readAsText(file);
  });

/* ───────────────────────────── markdown view ───────────────────────────── */

const SPEC_SX = {
  color: A.ink2,
  fontSize: 13.5,
  lineHeight: 1.6,
  overflowWrap: "anywhere",
  "& > :first-of-type": { mt: 0 },
  "& > :last-child": { mb: 0 },
  "& h1": { fontSize: 18, fontWeight: 700, color: A.ink, mt: 2, mb: 0.75 },
  "& h2": { fontSize: 15, fontWeight: 700, color: A.ink, mt: 2, mb: 0.75 },
  "& h3": { fontSize: 14, fontWeight: 700, color: A.ink, mt: 1.75, mb: 0.5 },
  "& h4, & h5, & h6": { fontSize: 13, fontWeight: 700, color: A.ink, mt: 1.5, mb: 0.5 },
  "& p": { my: 0.75 },
  "& ul, & ol": { pl: 2.75, my: 0.75 },
  "& li": { my: 0.25 },
  "& li.task-list-item": { listStyle: "none", ml: -2.25 },
  "& input[type=checkbox]": { mr: 0.75, verticalAlign: "-2px", accentColor: teal[600] },
  "& strong": { fontWeight: 700, color: A.ink },
  "& a": { color: A.tealDark, fontWeight: 600 },
  "& blockquote": {
    m: 0,
    my: 1,
    pl: 1.5,
    borderLeft: `3px solid ${A.lineStrong}`,
    color: A.muted,
  },
  "& code": {
    fontFamily: A.mono,
    fontSize: "0.86em",
    bgcolor: slate[100],
    color: A.ink,
    px: 0.5,
    py: "1px",
    borderRadius: "4px",
  },
  "& pre": {
    bgcolor: slate[900],
    color: slate[200],
    p: 1.5,
    borderRadius: "8px",
    overflowX: "auto",
    my: 1,
    fontSize: 12.5,
    "& code": { bgcolor: "transparent", color: "inherit", p: 0 },
  },
  "& hr": { border: 0, borderTop: `1px solid ${A.line}`, my: 2 },
  "& .md-table": { overflowX: "auto", my: 1 },
  "& table": { borderCollapse: "collapse", fontSize: 12.5, minWidth: "100%" },
  "& th, & td": {
    border: `1px solid ${A.line}`,
    px: 1,
    py: 0.5,
    textAlign: "left",
    verticalAlign: "top",
  },
  "& th": { bgcolor: slate[50], fontWeight: 700, color: A.ink, whiteSpace: "nowrap" },
};

const MD_COMPONENTS = {
  a: ({ node, children, ...props }) => (
    <a {...props} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
  table: ({ node, ...props }) => (
    <div className="md-table">
      <table {...props} />
    </div>
  ),
};

/**
 * The spec, formatted. react-markdown never renders raw HTML, so pasted HTML
 * shows as text rather than running. Checklist boxes are read-only here.
 */
export const SpecMarkdown = ({ markdown, sx }) => (
  <Box sx={{ ...SPEC_SX, ...sx }}>
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
      {markdown || ""}
    </ReactMarkdown>
  </Box>
);

SpecMarkdown.propTypes = { markdown: PropTypes.string, sx: PropTypes.object };

/* ───────────────────────────── attachments ───────────────────────────── */

const RemoveX = ({ name, onClick }) => (
  <IconButton
    className="rm"
    size="small"
    aria-label={`Remove ${name}`}
    onClick={onClick}
    sx={{
      position: "absolute",
      top: -8,
      right: -8,
      width: 22,
      height: 22,
      bgcolor: A.surface,
      border: `1px solid ${A.line}`,
      boxShadow: A.shadowSm,
      opacity: 0,
      "&:hover": { bgcolor: A.bg },
      "&:focus-visible": { opacity: 1 },
    }}
  >
    <CloseRounded sx={{ fontSize: 14 }} />
  </IconButton>
);

RemoveX.propTypes = { name: PropTypes.string, onClick: PropTypes.func.isRequired };

/** A non-image attachment: icon, name and size. */
export const FileChip = ({ file, onRemove }) => (
  <Box sx={{ position: "relative", "&:hover .rm": { opacity: 1 } }}>
    <Flx
      ac
      g={1}
      component={file.url ? "a" : "div"}
      href={file.url || undefined}
      download={file.url ? file.name : undefined}
      sx={{
        px: 1.25,
        py: 0.75,
        maxWidth: 260,
        border: `1px solid ${A.line}`,
        borderRadius: "8px",
        bgcolor: A.surface,
        color: A.ink,
        textDecoration: "none",
        fontSize: 12.5,
      }}
    >
      <InsertDriveFileOutlined sx={{ fontSize: 18, color: A.muted }} />
      <Box
        component="span"
        sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
      >
        {file.name}
      </Box>
      <Box component="span" sx={{ color: A.faint, whiteSpace: "nowrap" }}>
        {formatBytes(file.size)}
      </Box>
    </Flx>
    {onRemove ? <RemoveX name={file.name} onClick={onRemove} /> : null}
  </Box>
);

FileChip.propTypes = { file: PropTypes.object.isRequired, onRemove: PropTypes.func };

/** An image attachment: a 132px thumbnail with its name under it. */
const ImageTile = ({ file, onRemove, onOpen }) => (
  <Box sx={{ position: "relative", width: 132, "&:hover .rm": { opacity: 1 } }}>
    <Box
      component="button"
      type="button"
      onClick={onOpen}
      aria-label={`Open ${file.name}`}
      sx={{
        display: "block",
        width: 132,
        height: 92,
        p: 0,
        border: `1px solid ${A.line}`,
        borderRadius: "8px",
        overflow: "hidden",
        bgcolor: A.bg,
        cursor: "zoom-in",
      }}
    >
      <Box
        component="img"
        src={file.url}
        alt={file.name}
        sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
      />
    </Box>
    <Txt
      component="div"
      sx={{
        mt: 0.5,
        fontSize: 11.5,
        color: A.muted,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {file.name}
    </Txt>
    {onRemove ? <RemoveX name={file.name} onClick={onRemove} /> : null}
  </Box>
);

ImageTile.propTypes = {
  file: PropTypes.object.isRequired,
  onRemove: PropTypes.func,
  onOpen: PropTypes.func,
};

/* ───────────────────────────── the dialog ───────────────────────────── */

const Label = ({ children, required, end, htmlFor }) => (
  <Flx ac jb g={1} wrap sx={{ mb: 0.75 }}>
    <Box
      component={htmlFor ? "label" : "div"}
      htmlFor={htmlFor}
      sx={{ fontSize: 13, fontWeight: 600, color: A.ink }}
    >
      {children}
      {required ? (
        <Box component="span" sx={{ color: status.error.fg, ml: "2px" }}>
          *
        </Box>
      ) : null}
    </Box>
    {end}
  </Flx>
);

Label.propTypes = {
  children: PropTypes.node,
  required: PropTypes.bool,
  end: PropTypes.node,
  htmlFor: PropTypes.string,
};

const Hint = ({ children, end }) => (
  <Flx jb g={1.5} sx={{ mt: 0.75, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
    <Txt sx={{ fontSize: 12, color: A.muted, lineHeight: 1.45 }}>{children}</Txt>
    {end ? (
      <Txt sx={{ fontSize: 12, color: A.faint, whiteSpace: "nowrap" }}>{end}</Txt>
    ) : null}
  </Flx>
);

Hint.propTypes = { children: PropTypes.node, end: PropTypes.node };

const SPEC_PLACEHOLDER = `Paste markdown here (like a spec Claude wrote for you), drop in a .md file, or paste screenshots.

## What I need
- [ ] Add isDelayedPurchase to the loan model
- [ ] Return it from GET /loans/:id`;

const ORANGE = status.warning.fg;

/**
 * The handoff dialog. `blocker` is the current handoff when editing (null for
 * a new one). `onSubmit(blocker)` receives the full object; `onCancel` leaves
 * the card where it was.
 */
export const HandoffDialog = ({ open, item, blocker, onCancel, onSubmit, onOpenImages }) => {
  const { enqueueSnackbar } = useSnackbar();
  const narrow = useMediaQuery("(max-width:760px)");
  const phone = useMediaQuery("(max-width:640px)");
  const editing = !!blocker;

  const [type, setType] = useState(null);
  const [text, setText] = useState("");
  const [spec, setSpec] = useState("");
  const [specFile, setSpecFile] = useState(null);
  const [files, setFiles] = useState([]);
  const [tab, setTab] = useState("write");
  const [excluded, setExcluded] = useState([]); // task texts left off the card
  const [manual, setManual] = useState([]); // [{ t, done }]
  const [doneByText, setDoneByText] = useState({});
  const [taskDraft, setTaskDraft] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const summaryRef = useRef(null);
  const firstOptionRef = useRef(null);
  const mdPicker = useRef(null);
  const filePicker = useRef(null);

  // Fill from the current handoff (edit) or start empty, each time it opens.
  useEffect(() => {
    if (!open) return;
    const b = blocker;
    const derived = parseSpecTasks(b?.spec).map((x) => x.t);
    setType(b?.type || null);
    setText(b?.text || "");
    setSpec(b?.spec || "");
    setSpecFile(b?.specFile || null);
    setFiles(b?.files || []);
    setTab("write");
    setExcluded([]);
    setManual((b?.items || []).filter((x) => !derived.includes(x.t)));
    setDoneByText(Object.fromEntries((b?.items || []).map((x) => [x.t, x.done])));
    setTaskDraft("");
    setDragOver(false);
    // Focus: the first option for a new handoff, Summary when editing.
    setTimeout(() => (b ? summaryRef.current : firstOptionRef.current)?.focus(), 50);
  }, [open, blocker]);

  const derived = useMemo(() => parseSpecTasks(spec), [spec]);
  // Completed tasks stay completed while their text is unchanged.
  const tasks = useMemo(
    () => [
      ...derived.map((x) => ({
        t: x.t,
        done: doneByText[x.t] ?? x.done,
        fromDetails: true,
        included: !excluded.includes(x.t),
      })),
      ...manual.map((x) => ({ ...x, done: doneByText[x.t] ?? x.done, manual: true, included: true })),
    ],
    [derived, manual, excluded, doneByText],
  );
  const kept = tasks.filter((x) => x.included);
  const ready = !!type && !!text.trim();

  const addFiles = (list) => {
    const arr = [...(list || [])];
    if (arr.length) setFiles((prev) => [...prev, ...arr.map(toAttachment)]);
  };

  const importMarkdown = async (file) => {
    try {
      const body = await readText(file);
      setSpec((prev) => (prev.trim() ? `${prev.replace(/\s+$/, "")}\n\n${body}` : body));
      setSpecFile({ name: file.name, size: file.size });
      enqueueSnackbar(`${file.name} imported`, { variant: "success" });
    } catch {
      enqueueSnackbar(`Couldn't read ${file.name}`, { variant: "error" });
    }
  };

  /** Editor drop: .md / .txt go into the details, everything else attaches. */
  const dropOnEditor = (e) => {
    e.preventDefault();
    setDragOver(false);
    const list = [...(e.dataTransfer?.files || [])];
    list.filter(isMarkdownFile).forEach(importMarkdown);
    addFiles(list.filter((f) => !isMarkdownFile(f)));
  };

  const pasteOnEditor = (e) => {
    const imgs = [...(e.clipboardData?.items || [])]
      .filter((it) => it.kind === "file" && it.type.startsWith("image/"))
      .map((it) => it.getAsFile())
      .filter(Boolean);
    if (!imgs.length) return;
    e.preventDefault();
    addFiles(imgs);
  };

  const addTask = () => {
    const t = taskDraft.trim();
    if (!t || tasks.some((x) => x.t === t)) return;
    setManual((prev) => [...prev, { t, done: false }]);
    setTaskDraft("");
  };

  const submit = () => {
    if (!ready) return;
    const opt = BLOCKER_TYPES.find((o) => o.key === type);
    onSubmit({
      type,
      label: opt.label,
      text: text.trim(),
      spec,
      specFile: spec.trim() ? specFile : null,
      files,
      items: kept.map((x) => ({ t: x.t, done: !!x.done })),
    });
  };

  const imageFiles = files.filter(isImage);
  const footerText = ready
    ? `${kept.length} task${kept.length === 1 ? "" : "s"} · ${files.length} attachment${
        files.length === 1 ? "" : "s"
      }`
    : "Add what's needed and a summary to continue";
  const lines = spec ? spec.split(/\r?\n/).length : 0;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      fullScreen={phone}
      maxWidth={false}
      aria-labelledby="handoff-title"
      PaperProps={{
        sx: phone
          ? {}
          : {
              width: 760,
              maxWidth: "calc(100% - 32px)",
              maxHeight: "calc(100% - 48px)",
              borderRadius: "16px",
            },
      }}
    >
      <Flx column sx={{ height: phone ? "100%" : "auto", maxHeight: "inherit", minHeight: 0 }}>
        {/* Header */}
        <Flx
          jb
          g={2}
          sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: `1px solid ${A.line}`, flexShrink: 0 }}
        >
          <Flx column g={0.5} sx={{ minWidth: 0 }}>
            <Txt id="handoff-title" component="h2" sx={{ m: 0, fontSize: 19, fontWeight: 700, color: A.ink }}>
              {editing ? "Edit backend details" : "Move to Requires Backend Changes"}
            </Txt>
            <Txt component="div" sx={{ fontSize: 13, color: A.muted, minWidth: 0 }}>
              <Box component="span" sx={{ fontFamily: A.mono, fontSize: 12, mr: 1, color: A.ink2 }}>
                {itemRef(item)}
              </Box>
              {displayTitle(item?.title)}
            </Txt>
          </Flx>
          <IconButton size="small" aria-label="Close" onClick={onCancel} sx={{ alignSelf: "flex-start" }}>
            <CloseRounded sx={{ fontSize: 20 }} />
          </IconButton>
        </Flx>

        {/* Body */}
        <Flx column g={3} sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 3, py: 2.5 }}>
          {/* What's needed */}
          <Box>
            <Label required>What&apos;s needed</Label>
            <Box
              role="radiogroup"
              aria-label="What's needed"
              sx={{
                display: "grid",
                gridTemplateColumns: narrow ? "repeat(2, minmax(0, 1fr))" : "repeat(4, minmax(0, 1fr))",
                gap: 1,
              }}
            >
              {BLOCKER_TYPES.map((o, i) => {
                const on = type === o.key;
                return (
                  <Box
                    key={o.key}
                    ref={i === 0 ? firstOptionRef : undefined}
                    component="button"
                    type="button"
                    role="radio"
                    aria-checked={on}
                    onClick={() => {
                      setType(o.key);
                      if (!text.trim()) setTimeout(() => summaryRef.current?.focus(), 0);
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1.5,
                      py: 1.25,
                      textAlign: "left",
                      font: "inherit",
                      fontSize: 13,
                      fontWeight: 600,
                      color: on ? ORANGE : A.ink2,
                      bgcolor: on ? status.warning.bg : A.surface,
                      border: `1px solid ${on ? ORANGE : A.lineStrong}`,
                      boxShadow: on ? `0 0 0 3px ${alpha(ORANGE, 0.16)}` : "none",
                      borderRadius: "10px",
                      cursor: "pointer",
                      "&:hover": { borderColor: on ? ORANGE : A.muted },
                      "&:focus-visible": { outline: `2px solid ${alpha(ORANGE, 0.5)}`, outlineOffset: 1 },
                    }}
                  >
                    <o.Icon sx={{ fontSize: 18, flexShrink: 0 }} />
                    {o.label}
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Summary */}
          <Box>
            <Label required htmlFor="handoff-summary">
              Summary
            </Label>
            <InputBase
              id="handoff-summary"
              inputRef={summaryRef}
              fullWidth
              value={text}
              onChange={(e) => setText(e.target.value.slice(0, SUMMARY_MAX))}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.preventDefault();
              }}
              placeholder="One line on why it's blocked, e.g. The loan purpose list comes from the loan model"
              sx={{
                px: 1.5,
                py: 0.75,
                fontSize: 14,
                border: `1px solid ${A.lineStrong}`,
                borderRadius: "8px",
                "&.Mui-focused": { borderColor: A.teal, boxShadow: `0 0 0 3px ${A.tealSoft}` },
              }}
            />
            <Hint end={`${text.length}/${SUMMARY_MAX}`}>
              Shown on the card so everyone can see why it&apos;s waiting.
            </Hint>
          </Box>

          {/* Details (markdown) */}
          <Box>
            <Label
              end={
                <Flx ac g={1.5}>
                  <Flx
                    role="tablist"
                    aria-label="Details view"
                    sx={{
                      p: "2px",
                      gap: "2px",
                      borderRadius: "7px",
                      bgcolor: slate[100],
                      border: `1px solid ${A.line}`,
                    }}
                  >
                    {[
                      ["write", "Write"],
                      ["preview", "Preview"],
                    ].map(([k, l]) => (
                      <Box
                        key={k}
                        component="button"
                        type="button"
                        role="tab"
                        aria-selected={tab === k}
                        onClick={() => setTab(k)}
                        sx={{
                          px: 1,
                          py: "2px",
                          border: 0,
                          borderRadius: "5px",
                          font: "inherit",
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: "pointer",
                          bgcolor: tab === k ? A.surface : "transparent",
                          color: tab === k ? A.ink : A.muted,
                          boxShadow: tab === k ? A.shadowSm : "none",
                        }}
                      >
                        {l}
                      </Box>
                    ))}
                  </Flx>
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<FileUploadOutlined />}
                    onClick={() => mdPicker.current?.click()}
                  >
                    Import .md
                  </Button>
                </Flx>
              }
            >
              Details for backend
            </Label>
            <Box
              onDragOver={(e) => {
                if (![...(e.dataTransfer?.types || [])].includes("Files")) return;
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false);
              }}
              onDrop={dropOnEditor}
              sx={{
                position: "relative",
                border: `1px solid ${A.lineStrong}`,
                borderRadius: "8px",
                overflow: "hidden",
                "&:focus-within": { borderColor: A.teal, boxShadow: `0 0 0 3px ${A.tealSoft}` },
              }}
            >
              {specFile ? (
                <Flx
                  ac
                  jb
                  g={1}
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    bgcolor: slate[50],
                    borderBottom: `1px solid ${A.line}`,
                    fontSize: 12.5,
                    color: A.ink2,
                  }}
                >
                  <Flx ac g={0.75} sx={{ minWidth: 0 }}>
                    <DescriptionOutlined sx={{ fontSize: 16, color: A.muted }} />
                    <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      <strong>{specFile.name}</strong> imported · {formatBytes(specFile.size)}
                    </Box>
                  </Flx>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => {
                      setSpec("");
                      setSpecFile(null);
                    }}
                    sx={{ minWidth: 0, py: 0 }}
                  >
                    Clear
                  </Button>
                </Flx>
              ) : null}
              {tab === "write" ? (
                <Box
                  component="textarea"
                  aria-label="Details for backend"
                  value={spec}
                  onChange={(e) => setSpec(e.target.value)}
                  onPaste={pasteOnEditor}
                  placeholder={SPEC_PLACEHOLDER}
                  spellCheck={false}
                  sx={{
                    display: "block",
                    width: "100%",
                    minHeight: 260,
                    maxHeight: 460,
                    resize: "vertical",
                    border: 0,
                    outline: 0,
                    p: 1.5,
                    fontFamily: A.mono,
                    fontSize: 12.5,
                    lineHeight: 1.6,
                    color: A.ink,
                    bgcolor: A.surface,
                    "&::placeholder": { color: A.faint },
                  }}
                />
              ) : (
                <Box sx={{ minHeight: 260, maxHeight: 460, overflowY: "auto", p: 1.75 }}>
                  {spec.trim() ? (
                    <SpecMarkdown markdown={spec} />
                  ) : (
                    <Txt sx={{ fontSize: 13, color: A.faint }}>Nothing to preview yet.</Txt>
                  )}
                </Box>
              )}
              {dragOver ? (
                <Flx
                  column
                  center
                  g={0.5}
                  sx={{
                    position: "absolute",
                    inset: 0,
                    pointerEvents: "none",
                    bgcolor: alpha(steel.tint, 0.92),
                    border: `2px dashed ${steel.main}`,
                    borderRadius: "8px",
                    textAlign: "center",
                    px: 2,
                  }}
                >
                  <Txt sx={{ fontSize: 14, fontWeight: 700, color: steel.main }}>Drop to add</Txt>
                  <Txt sx={{ fontSize: 12.5, color: A.ink2 }}>
                    .md files go into the details · images become attachments
                  </Txt>
                </Flx>
              ) : null}
            </Box>
            <Hint end={`${lines} line${lines === 1 ? "" : "s"}`}>
              Markdown supported. Checklist lines (<code>- [ ] …</code>) become backend tasks.
            </Hint>
            <input
              ref={mdPicker}
              type="file"
              accept=".md,.markdown,.txt,text/markdown,text/plain"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) importMarkdown(f);
                e.target.value = "";
              }}
            />
          </Box>

          {/* Screenshots & files */}
          <Box
            onDragOver={(e) => {
              if ([...(e.dataTransfer?.types || [])].includes("Files")) e.preventDefault();
            }}
            onDrop={(e) => {
              e.preventDefault();
              addFiles(e.dataTransfer?.files);
            }}
          >
            <Label>Screenshots &amp; files</Label>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: phone ? "repeat(2, minmax(0, 1fr))" : "repeat(auto-fill, 132px)",
                gap: 1.5,
                alignItems: "start",
              }}
            >
              {imageFiles.map((f) => (
                <ImageTile
                  key={f.id || f.url}
                  file={f}
                  onOpen={() =>
                    onOpenImages?.(
                      imageFiles.map((x) => ({ id: x.id || x.url, name: x.name, url: x.url })),
                      imageFiles.indexOf(f),
                    )
                  }
                  onRemove={() => setFiles((prev) => prev.filter((x) => x !== f))}
                />
              ))}
              {files
                .filter((f) => !isImage(f))
                .map((f) => (
                  <Box key={f.id || f.url} sx={{ gridColumn: phone ? "span 2" : "span 2" }}>
                    <FileChip file={f} onRemove={() => setFiles((prev) => prev.filter((x) => x !== f))} />
                  </Box>
                ))}
              <Box
                component="button"
                type="button"
                onClick={() => filePicker.current?.click()}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.25,
                  width: phone ? "100%" : 132,
                  height: 92,
                  p: 1,
                  border: `1px dashed ${A.lineStrong}`,
                  borderRadius: "8px",
                  bgcolor: A.surface,
                  font: "inherit",
                  color: A.muted,
                  cursor: "pointer",
                  "&:hover": { borderColor: A.teal, color: A.tealDeep, bgcolor: A.tealSoft },
                }}
              >
                <AttachFileRounded sx={{ fontSize: 20 }} />
                <Box component="span" sx={{ fontSize: 12, fontWeight: 600 }}>
                  Add images or files
                </Box>
                <Box component="span" sx={{ fontSize: 11 }}>
                  or paste a screenshot
                </Box>
              </Box>
            </Box>
            <input
              ref={filePicker}
              type="file"
              multiple
              hidden
              onChange={(e) => {
                addFiles(e.target.files);
                e.target.value = "";
              }}
            />
          </Box>

          {/* Tasks */}
          <Box>
            <Label
              end={
                <Txt sx={{ fontSize: 12, color: A.muted }}>tracked as a checklist on the card</Txt>
              }
            >
              Backend tasks
            </Label>
            {tasks.length ? (
              <Box sx={{ border: `1px solid ${A.line}`, borderRadius: "8px", mb: 1 }}>
                {tasks.map((x, i) => (
                  <Flx
                    key={`${x.manual ? "m" : "d"}-${x.t}`}
                    ac
                    g={1}
                    sx={{
                      px: 1,
                      py: 0.25,
                      borderTop: i ? `1px solid ${A.line}` : "none",
                      fontSize: 13,
                    }}
                  >
                    {x.fromDetails ? (
                      <Checkbox
                        size="small"
                        checked={x.included}
                        onChange={(e) =>
                          setExcluded((prev) =>
                            e.target.checked ? prev.filter((t) => t !== x.t) : [...prev, x.t],
                          )
                        }
                        inputProps={{ "aria-label": `Include "${x.t}"` }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 14,
                          height: 14,
                          mx: "11px",
                          border: `1.5px solid ${A.lineStrong}`,
                          borderRadius: "3px",
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <Box
                      component="span"
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        color: x.included ? A.ink : A.faint,
                        textDecoration: x.included ? "none" : "line-through",
                      }}
                    >
                      {x.t}
                    </Box>
                    {x.fromDetails ? (
                      <Txt sx={{ fontSize: 11.5, color: A.faint, whiteSpace: "nowrap", pr: 1 }}>
                        from details
                      </Txt>
                    ) : (
                      <IconButton
                        size="small"
                        aria-label={`Remove task "${x.t}"`}
                        onClick={() => setManual((prev) => prev.filter((m) => m.t !== x.t))}
                      >
                        <CloseRounded sx={{ fontSize: 16 }} />
                      </IconButton>
                    )}
                  </Flx>
                ))}
              </Box>
            ) : (
              <Txt component="div" sx={{ fontSize: 12.5, color: A.faint, mb: 1 }}>
                No tasks yet. Add checklist lines to the details, or add them here.
              </Txt>
            )}
            <Flx g={1}>
              <InputBase
                fullWidth
                value={taskDraft}
                onChange={(e) => setTaskDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addTask();
                  }
                }}
                placeholder="Add a task"
                inputProps={{ "aria-label": "Add a task" }}
                sx={{
                  px: 1.25,
                  py: 0.25,
                  fontSize: 13.5,
                  border: `1px solid ${A.lineStrong}`,
                  borderRadius: "8px",
                  "&.Mui-focused": { borderColor: A.teal, boxShadow: `0 0 0 3px ${A.tealSoft}` },
                }}
              />
              <Button
                variant="outlined"
                color="gray"
                startIcon={<AddRounded />}
                disabled={!taskDraft.trim()}
                onClick={addTask}
                sx={{ flexShrink: 0 }}
              >
                Add
              </Button>
            </Flx>
          </Box>
        </Flx>

        {/* Footer */}
        <Flx
          ac
          jb
          g={1.5}
          sx={{ px: 3, py: 1.75, borderTop: `1px solid ${A.line}`, flexShrink: 0 }}
        >
          <Txt
            sx={{
              display: phone ? "none" : "block",
              fontSize: 12.5,
              color: ready ? A.muted : status.warning.fg,
            }}
          >
            {footerText}
          </Txt>
          <Flx ac g={1} sx={{ ml: "auto" }}>
            <Button variant="text" color="gray" onClick={onCancel}>
              Cancel
            </Button>
            <Button variant="contained" disabled={!ready} onClick={submit}>
              {editing ? "Save" : "Move to Requires Backend Changes"}
            </Button>
          </Flx>
        </Flx>
      </Flx>
    </Dialog>
  );
};

HandoffDialog.propTypes = {
  open: PropTypes.bool,
  item: PropTypes.object,
  blocker: PropTypes.object,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onOpenImages: PropTypes.func,
};

/* ───────────────────────── card detail: callout + spec ───────────────────────── */

const LinkButton = ({ children, onClick, startIcon }) => (
  <Button
    size="small"
    variant="text"
    startIcon={startIcon}
    onClick={onClick}
    sx={{ minWidth: 0, px: 1, py: 0.5, fontSize: 12, fontWeight: 600, borderRadius: "6px" }}
  >
    {children}
  </Button>
);

LinkButton.propTypes = {
  children: PropTypes.node,
  onClick: PropTypes.func,
  startIcon: PropTypes.node,
};

/**
 * The blocker callout near the top of the card detail while the card waits on
 * the backend: what's needed, who owns it, the summary, the task checklist
 * (ticking saves straight away), progress, and links to the spec / edit.
 */
export const BlockerCallout = ({ blocker, onToggleTask, onReadSpec, onEdit }) => {
  const items = blocker.items || [];
  const done = items.filter((x) => x.done).length;
  const hasSpec = !!blocker.spec?.trim();
  const nFiles = blocker.files?.length || 0;
  const opt = BLOCKER_TYPES.find((o) => o.key === blocker.type);
  const Icon = opt?.Icon || StorageRounded;
  // Folded by default: the header + summary say why it's blocked; the
  // progress, tasks and links open on demand.
  const [open, setOpen] = useState(false);
  return (
    <Box
      sx={{
        border: `1px solid ${alpha(ORANGE, 0.28)}`,
        bgcolor: status.warning.bg,
        borderRadius: "10px",
        overflow: "hidden",
      }}
    >
      <Box sx={{ px: 2, pt: 1.5, pb: open && items.length ? 1.25 : 1.5 }}>
        {/* What's needed (left) · progress (right). Click to fold / unfold. */}
        <Flx
          component="button"
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          ac
          jb
          g={1}
          sx={{
            width: "100%",
            p: 0,
            border: 0,
            bgcolor: "transparent",
            font: "inherit",
            textAlign: "left",
            cursor: "pointer",
            "&:focus-visible": { outline: `2px solid ${alpha(ORANGE, 0.4)}`, borderRadius: "4px" },
          }}
        >
          <Flx ac g={0.75} sx={{ color: ORANGE, fontWeight: 600, fontSize: 12 }}>
            <ExpandMoreRounded
              sx={{
                fontSize: 18,
                ml: "-3px",
                transition: "transform .15s",
                transform: open ? "none" : "rotate(-90deg)",
              }}
            />
            <Icon sx={{ fontSize: 16 }} />
            {blocker.label}
          </Flx>
          {items.length ? (
            <Txt sx={{ fontSize: 12, fontWeight: 600, color: A.muted, whiteSpace: "nowrap" }}>
              {done} of {items.length} done
            </Txt>
          ) : null}
        </Flx>
        <Txt component="div" sx={{ mt: 0.75, fontSize: 13, lineHeight: 1.5, color: A.ink }}>
          {blocker.text}
        </Txt>

        <Collapse in={open} unmountOnExit>
        {items.length ? (
          <>
            <Box
              sx={{
                mt: 1.25,
                height: 4,
                borderRadius: 99,
                bgcolor: alpha(ORANGE, 0.15),
                overflow: "hidden",
              }}
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={items.length}
              aria-valuenow={done}
            >
              <Box
                sx={{
                  width: `${(done / items.length) * 100}%`,
                  height: "100%",
                  bgcolor: status.success.solid,
                  transition: "width .2s",
                }}
              />
            </Box>
            <Flx column g={0.5} sx={{ mt: 1.25 }}>
              {items.map((x, i) => (
                <Flx
                  key={`${i}-${x.t}`}
                  component="label"
                  g={1}
                  sx={{ alignItems: "flex-start", cursor: "pointer" }}
                >
                  <Checkbox
                    size="small"
                    checked={!!x.done}
                    onChange={() => onToggleTask(i)}
                    sx={{ p: 0, mt: "1px", "& svg": { fontSize: 17 } }}
                  />
                  <Box
                    component="span"
                    sx={{
                      // One step below the 13px summary above.
                      fontSize: 12,
                      lineHeight: 1.45,
                      color: x.done ? A.muted : A.ink2,
                      textDecoration: x.done ? "line-through" : "none",
                    }}
                  >
                    {x.t}
                  </Box>
                </Flx>
              ))}
            </Flx>
          </>
        ) : null}
        </Collapse>
      </Box>

      {/* actions */}
      <Collapse in={open} unmountOnExit>
      <Flx
        ac
        jb
        g={1}
        sx={{ px: 1.25, py: 0.5, borderTop: `1px solid ${alpha(ORANGE, 0.18)}` }}
      >
        <Flx ac g={0.5} wrap>
          {hasSpec ? (
            <LinkButton startIcon={<DescriptionOutlined />} onClick={onReadSpec}>
              Read the spec
            </LinkButton>
          ) : (
            <LinkButton startIcon={<AddRounded />} onClick={onEdit}>
              Add details
            </LinkButton>
          )}
          {nFiles ? (
            <Txt sx={{ fontSize: 12, color: A.muted, px: 0.5 }}>
              {nFiles} attachment{nFiles === 1 ? "" : "s"}
            </Txt>
          ) : null}
        </Flx>
        <LinkButton startIcon={<EditOutlined />} onClick={onEdit}>
          Edit
        </LinkButton>
      </Flx>
      </Collapse>
    </Box>
  );
};

BlockerCallout.propTypes = {
  blocker: PropTypes.object.isRequired,
  onToggleTask: PropTypes.func.isRequired,
  onReadSpec: PropTypes.func,
  onEdit: PropTypes.func.isRequired,
};

const SPEC_COLLAPSED = 280;

/**
 * The BACKEND SPEC section: header with copy / download / edit, the formatted
 * spec collapsed to 280px (with a fade) until expanded, then the attachments.
 */
export const BackendSpecSection = ({ item, blocker, onEdit, onOpenImages, sectionRef }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const bodyRef = useRef(null);
  const spec = blocker.spec || "";
  const lines = spec ? spec.split(/\r?\n/).length : 0;
  const images = (blocker.files || []).filter(isImage);
  const others = (blocker.files || []).filter((f) => !isImage(f));

  useEffect(() => {
    setExpanded(false);
  }, [item.id]);
  useEffect(() => {
    const el = bodyRef.current;
    if (el) setOverflows(el.scrollHeight > SPEC_COLLAPSED + 8);
  }, [spec]);

  if (!spec.trim() && !blocker.files?.length) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(spec);
      enqueueSnackbar("Spec copied as markdown", { variant: "success" });
    } catch {
      enqueueSnackbar("Couldn't copy the spec", { variant: "error" });
    }
  };
  const download = () => {
    const name = blocker.specFile?.name || `${itemRef(item).toLowerCase()}-backend.md`;
    const url = URL.createObjectURL(new Blob([spec], { type: "text/markdown" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const iconBtn = (title, Icon, onClick) => (
    <Tooltip title={title} disableInteractive>
      <IconButton
        size="small"
        aria-label={title}
        onClick={onClick}
        sx={{ color: A.muted, p: "4px" }}
      >
        <Icon sx={{ fontSize: 17 }} />
      </IconButton>
    </Tooltip>
  );

  return (
    <Box ref={sectionRef} sx={{ scrollMarginTop: 16 }}>
      <CollapsibleSection
        title="Backend spec"
        meta={blocker.specFile?.name}
        end={
          <Flx ac g={0.25}>
            {spec.trim() ? iconBtn("Copy as markdown", ContentCopyRounded, copy) : null}
            {spec.trim() ? iconBtn("Download .md", FileDownloadOutlined, download) : null}
            {iconBtn("Edit backend details", EditOutlined, onEdit)}
          </Flx>
        }
      >

      {spec.trim() ? (
        <Box sx={{ border: `1px solid ${A.line}`, borderRadius: "10px", bgcolor: A.surface }}>
          <Box
            ref={bodyRef}
            sx={{
              position: "relative",
              px: 1.75,
              py: 1.5,
              maxHeight: expanded ? "none" : SPEC_COLLAPSED,
              overflow: "hidden",
            }}
          >
            <SpecMarkdown markdown={spec} />
            {!expanded && overflows ? (
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: 0,
                  height: 64,
                  background: `linear-gradient(to bottom, ${alpha(A.surface, 0)}, ${A.surface})`,
                  pointerEvents: "none",
                }}
              />
            ) : null}
          </Box>
          {overflows ? (
            <Box sx={{ borderTop: `1px solid ${A.line}`, px: 1, py: 0.25 }}>
              <LinkButton onClick={() => setExpanded((v) => !v)}>
                {expanded ? "Show less ▴" : `Show full spec · ${lines} lines ▾`}
              </LinkButton>
            </Box>
          ) : null}
        </Box>
      ) : null}

      {images.length || others.length ? (
        <Flx g={1.25} wrap sx={{ mt: 1.25, alignItems: "flex-start" }}>
          {images.map((f, i) => (
            <Box
              key={f.id || f.url}
              component="button"
              type="button"
              aria-label={`Open ${f.name}`}
              onClick={() =>
                onOpenImages(
                  images.map((x) => ({ id: x.id || x.url, name: x.name, url: x.url })),
                  i,
                )
              }
              sx={{
                width: 150,
                p: 0,
                border: 0,
                bgcolor: "transparent",
                textAlign: "left",
                cursor: "zoom-in",
                font: "inherit",
              }}
            >
              <Box
                component="img"
                src={f.url}
                alt={f.name}
                sx={{
                  display: "block",
                  width: 150,
                  height: 100,
                  objectFit: "cover",
                  borderRadius: "8px",
                  border: `1px solid ${A.line}`,
                }}
              />
              <Txt
                component="div"
                sx={{
                  mt: 0.5,
                  fontSize: 11.5,
                  color: A.muted,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {f.name}
              </Txt>
            </Box>
          ))}
          {others.map((f) => (
            <FileChip key={f.id || f.url || f.name} file={f} />
          ))}
        </Flx>
      ) : null}
      </CollapsibleSection>
    </Box>
  );
};

BackendSpecSection.propTypes = {
  item: PropTypes.object.isRequired,
  blocker: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onOpenImages: PropTypes.func.isRequired,
  sectionRef: PropTypes.object,
};
